import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { FrameworkCard } from '@/components/FrameworkCard';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { Search } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function WorkPage() {
  const [frameworks, setFrameworks] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  usePageTitle();

  useEffect(() => {
    api.get('/frameworks').then(res => {
      setFrameworks(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = frameworks.filter(fw =>
    fw.name.toLowerCase().includes(search.toLowerCase()) ||
    fw.tags?.some(t => t.toLowerCase().includes(search.toLowerCase())) ||
    fw.summary?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Page Header */}
      <section className="pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4"
            >
              Framework Library
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-6"
            >
              The Work
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-base sm:text-lg leading-[1.7] text-[hsl(var(--muted-foreground))] max-w-2xl mb-10"
            >
              A structured library of frameworks for understanding consciousness, identity, and power. Each framework is a tool for seeing what was always there.
            </motion.p>

            {/* Search */}
            <motion.div variants={fadeUp} className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search frameworks..."
                className="pl-10 bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:ring-[hsl(var(--gold))] font-['Inter'] text-sm"
                data-testid="framework-search"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Frameworks Grid */}
      <section className="pb-16 sm:pb-24 blueprint-grid">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <ConcentricRings size={60} opacity={0.3} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">No frameworks found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((fw, i) => (
                <motion.div
                  key={fw._id || fw.slug}
                  initial="hidden"
                  animate="visible"
                  custom={i}
                  variants={fadeUp}
                >
                  <FrameworkCard framework={fw} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
